import { Link, useNavigate, useParams } from 'react-router-dom';
import { LinkIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { TagInput } from '../ui/TagInput';
import { ChipSelect } from '../ui/ChipSelect';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { DeleteButton } from '../ui/DeleteButton';
import { deleteBlockedReason } from './catalogLinks';
import { undoToast } from '../ui/undoToast';
import { CheckList } from '../ui/CheckList';
import { NumberInput } from '../ui/NumberInput';
import { ViewOnSite } from './ViewOnSite';
import { PromoPanel } from './PromoPanel';
import { ProductMarginPanel } from './ProductMarginPanel';
import { ProductMediaPanel } from './ProductMediaPanel';
import { StockField } from './StockField';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { productSeoChecks } from './catalogSeo';
import { productSeoAuto } from '../../utils/seoAuto';
import { skinTypes } from '../../data/concerns';
import { PRODUCT_NOTE_MAX, TAG_SUGGESTIONS, emptyProduct, oneOf, productCategories, productChecks, routineSteps, uniqueSorted } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
const back = {
  to: '/be/producten',
  label: 'Alle producten'
};

/** Eén product: basis, huidthema's, teksten, koppelingen en voorraad. */
export function ProductEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: product,
    set
  } = useEditorItem('products', id, emptyProduct);
  if (!product) return <MissingItem title="Product niet gevonden" back={back} data-id="element-667" data-mp="15p80l5l3yza0" />;
  const takenIds = catalog.products.map(p => p.id);
  const suggestedId = uniqueSlug(slugify(`${product.brand} ${product.name}`), takenIds);
  // Een zelf getypt webadres dat al bestaat zou dat andere product overschrijven.
  const idTaken = isNew && !!product.id && takenIds.includes(product.id);
  const canAdd = product.name.trim() !== '' && product.price > 0 && !idTaken;
  const checks = [...productChecks(product), ...productSeoChecks(product, catalog.products)];
  const inRoutines = index.routinesWithProduct(product.id);
  const inArticles = catalog.articles.filter(a => a.productIds?.includes(product.id));
  // Verwijderen haalt hem ook uit protocollen en journalstukken; een protocol kan dan onder de twee zakken.
  const usedIn = [inRoutines.length ? plural(inRoutines.length, 'protocol', 'protocollen') : '', inArticles.length ? plural(inArticles.length, 'journalstuk', 'journalstukken') : ''].filter(Boolean);
  const thinRoutines = inRoutines.filter(r => r.productIds.length <= 2);
  // Als cadeau gekoppeld: dan zou de staffel naar een product wijzen dat niet meer bestaat. Eerst ontkoppelen.
  const giftLinks = site.giftTiers.filter(t => t.stockItemId === product.id || Object.values(t.choiceStockItems ?? {}).includes(product.id)).map(t => `cadeau “${t.title}”`);
  const concerns = index.concernsForProduct(product);
  const allIngredients = uniqueSorted(catalog.products.map(p => p.keyIngredients));
  const add = () => {
    if (!canAdd) return;
    const created = {
      ...product,
      id: product.id ? uniqueSlug(product.id, takenIds) : suggestedId
    };
    putItem('products', created);
    navigate(`/be/producten/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-668" data-mp="1340a9xa12zwf">
      <PageHeader title={isNew ? 'Nieuw product' : product.name || 'Naamloos product'} description={isNew ? 'Vul in wat je weet — lege velden verbergt de site vanzelf.' : `${product.brand} · ${product.category}`} back={back} actions={isNew ? <span className="flex flex-wrap items-center justify-end gap-3" data-id="element-670" data-mp="3vyiaspbss4kg">
              {!canAdd && <span className="text-[13px] text-muted" data-id="element-671" data-mp="3hyzfwy7zypq3">
                  {!product.name.trim() ? 'Vul een naam in' : !(product.price > 0) ? 'Vul een prijs in' : 'Dit webadres bestaat al'}
                </span>}
              <button type="button" onClick={add} disabled={!canAdd} className={btnPrimary} data-id="element-672" data-mp="387v1bzm8h6vs">
                Product toevoegen
              </button>
            </span> : <>
              <DeleteButton undoable blockedReason={deleteBlockedReason(giftLinks, 'Kies in Instellingen › Cadeaus & codes eerst een ander artikel.')} warning={`${usedIn.length ? `Zit in ${usedIn.join(' en ')}. Wordt daar ook weggehaald.${thinRoutines.length ? ` ${thinRoutines.map(r => r.name).join(', ')} houdt dan minder dan twee producten over.` : ''} ` : ''}Staat het in een abonnement, dan gaat het niet meer mee in de doos. Liever tijdelijk uit de winkel? Zet de voorraad op 0.`} onConfirm={() => {
        const restore = removeItem('products', product.id);
        navigate(back.to);
        undoToast(`“${product.name || 'Naamloos product'}” verwijderd`, restore, {
          description: usedIn.length ? `Ongedaan maken zet hem ook terug in ${usedIn.join(' en ')}.` : 'Pas definitief als je opslaat.',
          restored: 'Product teruggezet'
        });
      }} data-id="element-673" data-mp="2z9atjti7ohmc" />
              <ViewOnSite path={`/webshop/${product.id}`} data-id="element-674" data-mp="2yfa4w5y4mhl7" />
            </>} data-id="element-669" data-mp="133rvu0y9bmj4" />

      {/* Eén foto-paneel, door het grid geplaatst: op mobiel bovenaan (daar herken je het product
          aan), vanaf lg bovenaan de zijkolom. De `1fr`-rij vangt de hoogte van de lange formulierkolom
          op, zodat er geen gat valt tussen de foto en de rest van de zijkolom. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:grid-rows-[auto_1fr]" data-id="element-675" data-mp="3kbbsrcekwaa5">
        <Panel title="Productfoto" description="Een packshot op wit of transparant werkt het best." className="lg:col-span-4 lg:col-start-9 lg:row-start-1" data-id="element-676" data-mp="3pb6ihcmmattm">
          <div className="mx-auto max-w-[14rem] lg:max-w-none" data-id="element-677" data-mp="2wn3petqdcmm">
            <ImageField value={product.image} onChange={image => set({
            image
          })} data-id="element-678" data-mp="6xqg1rckkqj3" />
          </div>
        </Panel>

        <div className="min-w-0 space-y-6 lg:col-span-8 lg:col-start-1 lg:row-span-2 lg:row-start-1" data-id="element-679" data-mp="bywc9z5fuvxd">
          <Panel title="Basis" data-id="element-680" data-mp="gw5yl5xj6rgz">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-681" data-mp="gubn0i85gbpk">
              <Field label="Naam" className="sm:col-span-2" data-id="element-682" data-mp="2dq9fmjrrcvrb">
                <input value={product.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} placeholder="Bijv. Crystal Retinal 1" data-id="element-683" data-mp="2inssbf8t50h5" />
              </Field>
              <Field label="Merk" data-id="element-684" data-mp="2b6csb92au03s">
                <select value={product.brand} onChange={e => set({
                brand: e.target.value
              })} className={inputCls} data-id="element-685" data-mp="26d9vms286ajd">
                  {catalog.brands.map(b => <option key={b.name} data-id="element-686" data-mp="21cds4flscqeh">{b.name}</option>)}
                </select>
              </Field>
              <Field label={product.promo ? 'Actieprijs (€, incl. btw)' : 'Prijs (€, incl. btw)'} hint={product.promo ? 'Pas je aan bij Leveranciersactie.' : 'Adviesprijs van het merk, nooit korting.'} data-id="element-687" data-mp="2u34b7hdxid7b">
                <NumberInput decimals value={product.price || undefined} onChange={price => set({
                price: price ?? 0
              })} emptyMessage="Vul een prijs in, bijv. 49,95." placeholder="49,95" data-id="element-688" data-mp="2k3dcf3ic5c1f" />
              </Field>
              <Field label="Soort product" data-id="element-689" data-mp="13t2l2sxf3kq1">
                <select value={product.category} onChange={e => {
                const category = oneOf(productCategories, e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-690" data-mp="1teu89pc9g2zy">
                  {productCategories.map(c => <option key={c} data-id="element-691" data-mp="2c5lfolcotjbp">{c}</option>)}
                </select>
              </Field>
              <Field label="Stap in de routine" hint="Bepaalt de volgorde en het filter in de webshop." data-id="element-692" data-mp="2h4et6drz5abv">
                <select value={product.step ?? ''} onChange={e => set({
                step: oneOf(routineSteps, e.target.value)
              })} className={inputCls} data-id="element-693" data-mp="23dt8cjiymqbc">
                  <option value="" data-id="element-694" data-mp="27dbufimip7fr">{product.step ? 'Geen stap' : 'Kies een stap'}</option>
                  {routineSteps.map(s => <option key={s} data-id="element-695" data-mp="2zv60zawbcso5">{s}</option>)}
                </select>
              </Field>
              <Field label="Inhoud" hint="Bijv. 30 ml. Leeg = niet getoond." data-id="element-696" data-mp="ofxagdjam0me">
                <input value={product.volume ?? ''} onChange={e => set({
                volume: e.target.value || undefined
              })} className={inputCls} placeholder="30 ml" data-id="element-697" data-mp="odugjif0rnce" />
              </Field>
              <Field label="Gaat mee" hint="Bijv. ± 3 maanden. Leeg = niet getoond." data-id="element-698" data-mp="y58mo91h7lei">
                <input value={product.usesPerBottle ?? ''} onChange={e => set({
                usesPerBottle: e.target.value || undefined
              })} className={inputCls} placeholder="± 3 maanden" data-id="element-699" data-mp="t7fe59cpafb2" />
              </Field>
            </div>
          </Panel>

          <Panel title="Huidthema’s" description="Hiermee verschijnt het product in de filters, de klachtenkiezer en het huidadvies." data-id="element-700" data-mp="fdfswcel1w5q">
            <ChipSelect options={skinTypes} value={product.skinTypes} onChange={next => set({
            skinTypes: next
          })} meta={s => String(index.productsForSkinType(s).filter(p => p.id !== product.id).length)} data-id="element-701" data-mp="1qu5zhwmx4pzv" />
            {concerns.length > 0 && <p className="mt-4 flex items-start gap-2 text-[13px] leading-relaxed text-muted" data-id="element-702" data-mp="2c48khcz9pt7w">
                <LinkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-703" data-mp="27bfg4q7pomwc" />
                Wordt aanbevolen bij: {concerns.map(c => c.label.toLowerCase()).join(', ')}.
              </p>}
          </Panel>

          <Panel title="Teksten" description="In je eigen woorden — zo staat het op de productpagina." data-id="element-704" data-mp="1tcpjtcspur91">
            <div className="space-y-5" data-id="element-705" data-mp="1odcwkqxxni5x">
              <Field label="Korte omschrijving" hint="Eén zin onder de naam, op de productkaart." count={{
              value: product.note.length,
              max: PRODUCT_NOTE_MAX
            }} data-id="element-706" data-mp="1oee7mvey84nn">
                <input value={product.note} onChange={e => set({
                note: e.target.value
              })} className={inputCls} data-id="element-707" data-mp="3irdt3560ft2u" />
              </Field>
              <Field label="Waarom ik dit kies" hint="Je persoonlijke verhaal: voor wie, wat je ziet in de salon." data-id="element-708" data-mp="3dtuh6bwsbse2">
                <textarea value={product.why} onChange={e => set({
                why: e.target.value
              })} rows={5} className={`${inputCls} resize-y leading-relaxed`} data-id="element-709" data-mp="3lbsab5unsqv4" />
              </Field>
              <Field label="Zo gebruik je het" data-id="element-710" data-mp="3v2wseefmsru6">
                <textarea value={product.howTo} onChange={e => set({
                howTo: e.target.value
              })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-711" data-mp="3v47vsd55zxkr" />
              </Field>
              <Field label="Belangrijkste ingrediënten" as="div" data-id="element-712" data-mp="32f0y1gdjbc0k">
                <TagInput label="Belangrijkste ingrediënten" value={product.keyIngredients} onChange={keyIngredients => set({
                keyIngredients
              })} suggestions={allIngredients.filter(i => !product.keyIngredients.includes(i)).slice(0, TAG_SUGGESTIONS)} data-id="element-713" data-mp="37dc3zg0pl2di" />
              </Field>
            </div>
          </Panel>

          <ProductMediaPanel value={product.media ?? []} onChange={media => set({
          media
        })} data-id="element-714" data-mp="wj6ndw21krcq" />

          <Panel title="Adviseer ik hierbij" description="Verschijnt als “Samen versterken ze elkaar” op de productpagina en in de winkelmand." data-id="element-715" data-mp="2smbcv65lcxk4">
            <ProductPicker value={product.pairs} onChange={pairs => set({
            pairs
          })} products={catalog.products} exclude={[product.id]} emptyText="Nog geen producten die je hierbij adviseert." data-id="element-716" data-mp="2nqnlql7jtubz" />
          </Panel>

          {product.kind !== 'giftcard' && <SeoPanel seo={product.seo} onChange={seo => set({
          seo
        })} auto={productSeoAuto(product)} path={product.id ? `/webshop/${product.id}` : '/webshop'} imageFallback="de productfoto" data-id="element-717" data-mp="24yl2r37fq1yk" />}
        </div>

        <aside aria-label="Actie, controle en zichtbaarheid" className="min-w-0 space-y-6 lg:col-span-4 lg:col-start-9 lg:row-start-2" data-id="element-718" data-mp="1q844fsq3js5w">
          <PromoPanel product={product} set={set} data-id="element-719" data-mp="1m722330vuzzp" />

          {!isNew && <ProductMarginPanel product={product} data-id="element-720" data-mp="1c2c6dew2os3x" />}

          <Panel title="Wat mist er nog?" data-id="element-721" data-mp="1c1itrxafn8dv">
            <CheckList checks={checks} okText="Dit product is compleet" data-id="element-722" data-mp="1guc3kzkrd6wt" />
          </Panel>

          <Panel title="Zichtbaarheid" data-id="element-723" data-mp="125z25n2wivux">
            <div className="space-y-1" data-id="element-724" data-mp="1270o4mlgh2rf">
              <Toggle checked={Boolean(product.bestseller)} onChange={bestseller => set({
              bestseller
            })} label="Bestseller" description="Op de homepage in “Liever met één product beginnen?”." data-id="element-725" data-mp="1740tswee83i0" />
              <Toggle checked={Boolean(product.salonFavourite)} onChange={salonFavourite => set({
              salonFavourite
            })} label="Favoriet uit de salon" description="Zelfde plekken op de homepage als een bestseller. Je eigen plank op Over Sarah kies je los, in Paginateksten › Over Sarah." data-id="element-726" data-mp="1zqln42efipo7" />
              <Toggle checked={product.refill} onChange={refill => set({
              refill
            })} label="Automatisch bij te vullen" description="Klant kan het in het Bewust Abonnement zetten." data-id="element-727" data-mp="egekd7ihi4n8" />
            </div>
          </Panel>

          {inRoutines.length > 0 && <Panel title="Zit in protocollen" data-id="element-728" data-mp="kulnkffl0c4p">
              <ul className="space-y-2 text-[14px]" data-id="element-729" data-mp="fywk410fyb34">
                {inRoutines.map(r => <li key={r.id} data-id="element-730" data-mp="1dkki2qcjp1gr">
                    <Link to={`/be/protocollen/${r.id}`} className="text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-731" data-mp="1dku7abprn4le">
                      {r.name}
                    </Link>
                    <span className="text-muted" data-id="element-732" data-mp="13luzevctrczg"> · {euro(index.routineTotals(r).price)}</span>
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] text-muted" data-id="element-733" data-mp="3oe6j3091b06o">De protocolprijs past zich vanzelf aan je prijs aan.</p>
            </Panel>}

          <Panel title="Koppeling & voorraad" data-id="element-734" data-mp="3jd0rj5r41iyq">
            <div className="space-y-4" data-id="element-735" data-mp="1ywt84owkl0ey">
              <Field label="Webadres" hint={idTaken ? 'Dit webadres is al van een ander product. Kies een ander.' : isNew ? 'Wordt vanzelf gemaakt uit merk en naam.' : 'Staat vast, zodat oude links en Google blijven werken.'} data-id="element-736" data-mp="1yxug2kiah6sg">
                <input value={isNew ? product.id || suggestedId : product.id} onChange={e => isNew && set({
                id: slugify(e.target.value)
              })} disabled={!isNew} className={`${inputCls} font-mono text-[13px]`} data-id="element-737" data-mp="2ijqteq9dfij4" />
              </Field>
              <Field label="Omschrijving van de foto" hint="Voor blinde bezoekers en Google. Leeg = merk en naam." data-id="element-738" data-mp="2wjiflea1k587">
                <input value={product.imageAlt ?? ''} onChange={e => set({
                imageAlt: e.target.value || undefined
              })} className={inputCls} data-id="element-739" data-mp="x9inareb3a0u" />
              </Field>
              <Field label="Barcode (EAN)" hint="Staat op de doos. Helpt Google Shopping." data-id="element-740" data-mp="u6ck3pi2l90l">
                <input value={product.gtin ?? ''} onChange={e => set({
                gtin: e.target.value.replace(/\D/g, '') || undefined
              })} inputMode="numeric" maxLength={14} className={`${inputCls} font-mono text-[13px]`} data-id="element-741" data-mp="q50yrw68b9y7" />
              </Field>
              {isNew ? <Field label="Beginvoorraad" hint="Leeg = niet bijhouden. Daarna pas je de voorraad hier los aan." data-id="element-742" data-mp="1933n08qyd2uy">
                  <NumberInput allowEmpty value={product.stock} onChange={stock => set({
                stock
              })} placeholder="Niet bijhouden" data-id="element-743" data-mp="1mui4uc94iy0f" />
                </Field> : <StockField key={product.id} productId={product.id} stock={product.stock} data-id="element-744" data-mp="1ws6b9zg3dtyf" />}
              {/* Technisch nummer: vult de koppeling zelf in. Alleen tonen als het er is, zodat niemand het met de hand gaat typen. */}
              {product.wooId !== undefined && <p className="text-[12px] text-muted" data-id="element-745" data-mp="2fgqc35s9ykil">
                  WooCommerce-nummer <span className="font-mono text-ink" data-id="element-746" data-mp="2fem5uh6wl9f5">{product.wooId}</span>
                </p>}
            </div>
          </Panel>
        </aside>
      </div>
    </div>;
}